import React from 'react';
import { BookingRequest, RequestClass } from 'acscp/api/requests';
import { RouteComponentProps } from 'react-router';
import { PageTitle } from '../common/pageTitle';
import { withPageLayout } from '../layout/pageLayout';
import { Dispatch } from 'redux';
import { connectWithRequests } from 'acscp/store/helpers/connectWithRequests';
import { defineMessages, FormattedMessage } from 'react-intl';
import { AddNotification, FetchRequest } from 'acscp/actions';
import { AppState, Booking } from 'acscp/store';
import { PresentationComponentBase } from '../presentationComponentBase';
import { FlightRequestForm } from '../flightRequest/form/flightRequestForm';
import { RequestStateProps } from 'acscp/components/common/helpers/requestStateProps';
import { LoadingIndicator } from 'acscp/components/common/loadingIndicator';
import { userCanRequestFlights, userIsLoggedIn } from 'acscp/store/helpers/authHelper';
import { NotificationType } from 'acscp/store/notificationState';
import { History } from 'history';
import { LoginRequiredModal } from 'acscp/components/auth/loginRequiredModal';
import { home } from 'acscp/components/routeHelper';

const messages = defineMessages({
    insufficientAuthentication: {
        id: 'components.pages.flightRequest.insufficientAuthentication',
        defaultMessage: 'You do not have access to that resource.',
    }
});

interface FlightRequestStateProps {
    isLoggedIn: boolean;
    canRequestFlights: boolean;
    requestState?: RequestStateProps;
    booking: Booking | undefined;
}

interface FlightRequestDispatchProps {
    fetchBooking: () => void;
    notifyAboutInsufficientAuthentication: () => void;
}

interface FlightRequestPageOwnProps {
    flightRequestId: string | undefined;
    isRepeatRequest: boolean;
    appHistory: History<any>;
}

export class FlightRequestPagePresentational extends PresentationComponentBase<
    FlightRequestPageOwnProps,
    FlightRequestStateProps,
    FlightRequestDispatchProps,
    {}
> {

    componentDidMount(): void {
        if (this.props.canRequestFlights) {
            this.props.fetchBooking();
        } else {
            if (this.props.isLoggedIn) {
                this.props.notifyAboutInsufficientAuthentication();
                this.props.appHistory.push(home());
            }
        }
    }

    render(): JSX.Element {
        if (this.props.isLoggedIn) {
            return (
                <div>
                    <section>
                        <PageTitle
                            title={
                                <FormattedMessage
                                    id={'components.pages.flightRequest.title'}
                                    defaultMessage={'NEW ENQUIRY'}
                                />}
                        />
                        {this.props.requestState && this.props.requestState.isLoading && (
                            <LoadingIndicator/>)}
                        {!this.props.flightRequestId || (this.props.requestState && this.props.requestState.success)
                            ? <FlightRequestForm
                                booking={this.props.booking}
                                isRepeatRequest={this.props.isRepeatRequest}
                                onModified={() => {
                                }}
                            />
                            : null}
                    </section>
                </div>
            );
        } else {
            return (
                <LoginRequiredModal closeUrl="/"/>
            );
        }
    }
}

function mapStateToProps(
    state: AppState, ownProps: FlightRequestPageOwnProps, getRequestError: (request: RequestClass<any>) => RequestStateProps
): FlightRequestStateProps {
    const isLoggedIn = userIsLoggedIn(state.auth);
    const canRequestFlights = userCanRequestFlights(state.auth);

    if (!ownProps.flightRequestId) {
        return {
            isLoggedIn,
            canRequestFlights,
            booking: undefined
        };
    }

    const bookingDetails = state.bookingsDetails[ownProps.flightRequestId];
    return {
        isLoggedIn,
        canRequestFlights,
        requestState: getRequestError(BookingRequest),
        booking: bookingDetails
    };
}

function mapDispatchToProps(dispatch: Dispatch, ownProps: FlightRequestPageOwnProps): FlightRequestDispatchProps {
    let fetchBooking: () => void;
    if (ownProps.flightRequestId !== undefined) {
        const flightRequestId = ownProps.flightRequestId;
        fetchBooking = () => dispatch(new FetchRequest(new BookingRequest(flightRequestId)));
    } else {
        fetchBooking = () => {};
    }

    return {
        fetchBooking,
        notifyAboutInsufficientAuthentication:
            () => dispatch(new AddNotification(
                'flightRequestInsufficientAuthentication', messages.insufficientAuthentication, NotificationType.Error))
    };
}

const FlightRequest = withPageLayout(connectWithRequests(mapStateToProps, mapDispatchToProps)(FlightRequestPagePresentational));

export interface FlightRequestPageParams {
    flightRequestId: string | undefined;
}

const NewFlightRequest: React.SFC<RouteComponentProps<any>> = props =>
    <FlightRequest flightRequestId={undefined} isRepeatRequest={false} appHistory={props.history} />;

const EditFlightRequest: React.SFC<RouteComponentProps<FlightRequestPageParams>> = props =>
    <FlightRequest flightRequestId={props.match.params.flightRequestId} isRepeatRequest={false} appHistory={props.history} />;

const RepeatFlightRequest: React.SFC<RouteComponentProps<FlightRequestPageParams>> = props =>
<FlightRequest flightRequestId={props.match.params.flightRequestId} isRepeatRequest={true} appHistory={props.history} />;

export { NewFlightRequest, EditFlightRequest, RepeatFlightRequest };
